import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  ButtonGroup
} from 'reactstrap';
import { Line } from 'react-chartjs-2';
import axios from 'axios';
import { Select, MenuItem, TextField } from '@mui/material';

const CallsAnalytics = ({ accessType, workspaceId }) => {
  // State management
  const [period, setPeriod] = useState('30d');
  const [dateRange, setDateRange] = useState({
    start: moment().subtract(30, 'days').startOf('day').toDate(),
    end: moment().endOf('day').toDate()
  });
  const [callsAnalytics, setCallsAnalytics] = useState(null);
  const [workspaceCallCosts, setWorkspaceCallCosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [error, setError] = useState(null);

  // Fetch calls analytics
  const fetchCallsAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ period });
      if (dateRange.start) params.append('startDate', dateRange.start.toISOString());
      if (dateRange.end) params.append('endDate', dateRange.end.toISOString());
      params.append('_t', Date.now()); // Cache buster

      const response = await axios.get(`/admin/billing/calls-analytics?${params}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      // Backend sendResponse uses 'body' field, not 'data'
      if (response.data && response.data.body) {
        setCallsAnalytics(response.data.body);
      }
    } catch (error) {
      console.error('Error fetching calls analytics:', error);
      setError(error.response?.data?.message || 'Failed to load calls analytics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch workspace costs
  const fetchWorkspaceCallCosts = async () => {
    try {
      const params = new URLSearchParams({
        period,
        page: currentPage,
        limit: 20,
        search: searchTerm
      });
      params.append('_t', Date.now()); // Cache buster

      const response = await axios.get(`/admin/billing/calls-analytics/workspaces?${params}`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      // Backend sendResponse uses 'body' field, not 'data'
      if (response.data && response.data.body) {
        setWorkspaceCallCosts(response.data.body.workspaces || []);
        setPagination(response.data.body.pagination || { page: 1, limit: 20, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching workspace costs:', error);
      setWorkspaceCallCosts([]);
    }
  };

  // Export data
  const handleExport = async (format = 'csv') => {
    try {
      const params = new URLSearchParams({ format, period });
      const response = await axios.get(`/admin/billing/calls-analytics/export?${params}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `calls-export-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    const days = parseInt(newPeriod.replace('d', ''));
    setDateRange({
      start: moment().subtract(days, 'days').startOf('day').toDate(),
      end: moment().endOf('day').toDate()
    });
  };

  // Effects
  useEffect(() => {
    fetchCallsAnalytics();
    fetchWorkspaceCallCosts();
  }, [period, dateRange, currentPage, searchTerm]);

  // Chart data
  const timeSeriesData = {
    labels: callsAnalytics?.timeSeriesData?.map(d => d.date) || [],
    datasets: [
      {
        label: 'Actual Cost ($)',
        data: callsAnalytics?.timeSeriesData?.map(d => d.actualCost) || [],
        borderColor: '#F7931A',
        backgroundColor: '#F7931A',
        yAxisID: 'y',
        tension: 0.4
      },
      {
        label: 'Charged Price ($)',
        data: callsAnalytics?.timeSeriesData?.map(d => d.chargedPrice) || [],
        borderColor: '#7882E0',
        backgroundColor: '#7882E0',
        yAxisID: 'y',
        tension: 0.4
      },
      {
        label: 'Calls Volume',
        data: callsAnalytics?.timeSeriesData?.map(d => d.calls) || [],
        type: 'bar',
        borderColor: '#261E69',
        backgroundColor: '#261E69',
        yAxisID: 'y1'
      }
    ]
  };

  return (
    <div className="calls-analytics-tab content-dashboard">
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <strong>Error:</strong> {error}
          <button type="button" className="close" onClick={() => setError(null)}>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      <h5 className="mb-3">Workspace Calls Analytics</h5>
      {/* Summary Cards */}
      <div className="counter-sections mb-4">
        <div className="counter-box style-two">
          <div className="counter-start">
            <div className="left-text-area">
              <h5>Total Calls</h5>
              <h3 className="text-primary">
                {callsAnalytics?.summary?.totalCalls?.toLocaleString() || 0}
              </h3>
              <small className={callsAnalytics?.summary?.callsGrowth >= 0 ? 'text-success' : 'text-danger'}>
                <i className={`fa fa-arrow-${callsAnalytics?.summary?.callsGrowth >= 0 ? 'up' : 'down'} mr-1`}></i>
                {Math.abs(callsAnalytics?.summary?.callsGrowth || 0).toFixed(1)}%
              </small>
            </div>
            <div className="right-icon">
              <span className="icon">
                <i className="fa fa-phone text-primary" style={{ fontSize: '20px' }}></i>
              </span>
            </div>
          </div>
        </div>

        <div className="counter-box style-two">
          <div className="counter-start">
            <div className="left-text-area">
              <h5>Total Minutes</h5>
              <h3 className="text-info">
                {callsAnalytics?.summary?.totalMinutes?.toLocaleString() || 0}
              </h3>
              <small className={callsAnalytics?.summary?.minutesGrowth >= 0 ? 'text-success' : 'text-danger'}>
                <i className={`fa fa-arrow-${callsAnalytics?.summary?.minutesGrowth >= 0 ? 'up' : 'down'} mr-1`}></i>
                {Math.abs(callsAnalytics?.summary?.minutesGrowth || 0).toFixed(1)}%
              </small>
            </div>
            <div className="right-icon">
              <span className="icon">
                <i className="fa fa-clock text-info" style={{ fontSize: '20px' }}></i>
              </span>
            </div>
          </div>
        </div>

        <div className="counter-box style-two">
          <div className="counter-start">
            <div className="left-text-area">
              <h5>Actual Cost</h5>
              <h3 className="text-warning">
                ${callsAnalytics?.summary?.totalActualCost?.toFixed(2) || '0.00'}
              </h3>
              <small className="text-muted">Provider charges</small>
            </div>
            <div className="right-icon">
              <span className="icon">
                <i className="fa fa-dollar-sign text-warning" style={{ fontSize: '18px' }}></i>
              </span>
            </div>
          </div>
        </div>

        <div className="counter-box style-two">
          <div className="counter-start">
            <div className="left-text-area">
              <h5>Charged / Profit</h5>
              <h3 className="text-success">
                ${callsAnalytics?.summary?.totalChargedPrice?.toFixed(2) || '0.00'}
              </h3>
              <small className="text-success">
                ${callsAnalytics?.summary?.totalProfit?.toFixed(2) || '0.00'}
                ({callsAnalytics?.summary?.profitMargin?.toFixed(1) || 0}%)
              </small>
            </div>
            <div className="right-icon">
              <span className="icon">
                <i className="fa fa-chart-line text-success" style={{ fontSize: '20px' }}></i>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="table-container filters-table mb-4">
        <div className='header-table-filters advanced-table-filters'>
          <div className='left-area' style={{ width: '60%' }}>
            <div className='filter-box'>
              <div className='searchbox'>
                <TextField
                  fullWidth
                  hiddenLabel
                  size="small"
                  name='search'
                  variant="standard"
                  placeholder="Search workspaces..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className='icon'>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                    <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                  </svg>
                </i>
              </div>
            </div>
            <div className='filter-box d-flex gap-3'>
              {/* <ButtonGroup style={{
                    backgroundColor: '#2C3E50',
                    borderRadius: '8px',
                    padding: '6px',
                    gap: '0'
                  }}>
                    <Button
                      style={{
                        backgroundColor: period === '7d' ? '#6366F1' : 'transparent',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 20px',
                        fontWeight: period === '7d' ? '600' : '400',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handlePeriodChange('7d')}
                    >
                      Daily (7d)
                    </Button>
                    <Button
                      style={{
                        backgroundColor: period === '14d' ? '#6366F1' : 'transparent',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 20px',
                        fontWeight: period === '14d' ? '600' : '400',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handlePeriodChange('14d')}
                    >
                      Weekly (14d)
                    </Button>
                    <Button
                      style={{
                        backgroundColor: period === '30d' ? '#6366F1' : 'transparent',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 20px',
                        fontWeight: period === '30d' ? '600' : '400',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handlePeriodChange('30d')}
                    >
                      Monthly (30d)
                    </Button>
                    <Button
                      style={{
                        backgroundColor: period === '365d' ? '#6366F1' : 'transparent',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 20px',
                        fontWeight: period === '365d' ? '600' : '400',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => handlePeriodChange('365d')}
                    >
                      All Time (1y)
                    </Button>
                  </ButtonGroup> */}
              <Select
                value={period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                displayEmpty
              >
                <MenuItem value="7d" >
                  Daily (7d)
                </MenuItem>
                <MenuItem value="14d">
                  Weekly (14d)
                </MenuItem>
                <MenuItem value="30d">
                  Monthly (30d)
                </MenuItem>
                <MenuItem value="365d">
                  All Time (1y)
                </MenuItem>
              </Select>
            </div>
          </div>
          <div className='right-deals-filtes' style={{ width: '40%' }}>
            <button
              className="btn-style-new"
              onClick={() => handleExport('csv')}
            >
              <i className="fa fa-download mr-2"></i>
              Export CSV
            </button>
          </div>
        </div>
        {/* Workspace Breakdown Table - Simplified Single Period View */}
        <div className='table-area'>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Workspace Name</th>
                <th style={{ minWidth: '200px' }}>Workspace ID</th>
                <th className="text-center">Calls / Agents</th>
                <th className="text-right">LLM Cost</th>
                <th className="text-right">STT Cost</th>
                <th className="text-right">TTS Cost</th>
                <th className="text-right">Total Cost</th>
                <th className="text-right">Charged Price</th>
                <th className="text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {workspaceCallCosts?.map(ws => (
                <tr key={ws.workspaceId}>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className="fa fa-building mr-2 text-muted"></i>
                      <strong>{ws.workspaceName}</strong>
                    </div>
                  </td>
                  <td>
                    <small className="text-muted font-monospace" style={{ fontSize: '0.75rem' }}>
                      {ws.workspaceId?.toString()}
                    </small>
                  </td>
                  <td className="">
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                      <Badge color="primary" pill>
                        <i className="fa fa-phone mr-1" style={{ fontSize: '0.7rem' }}></i>
                        {ws.totalCalls || 0}
                      </Badge>
                      <Badge color="info" pill>
                        <i className="fa fa-users mr-1" style={{ fontSize: '0.7rem' }}></i>
                        {ws.totalAgents || 0}
                      </Badge>
                    </div>
                  </td>
                  <td className="text-right">
                    <small className="text-primary">${(ws.llmCost || 0).toFixed(4)}</small>
                  </td>
                  <td className="text-right">
                    <small className="text-success">${(ws.sttCost || 0).toFixed(4)}</small>
                  </td>
                  <td className="text-right">
                    <small className="text-warning">${(ws.ttsCost || 0).toFixed(4)}</small>
                  </td>
                  <td className="text-right">
                    <strong className="text-danger">${(ws.totalCost || 0).toFixed(4)}</strong>
                  </td>
                  <td className="text-right">
                    <strong className="text-info">${(ws.chargedPrice || 0).toFixed(2)}</strong>
                  </td>
                  <td className="text-right">
                    <strong className={ws.profit >= 0 ? 'text-success' : 'text-danger'}>
                      ${(ws.profit || 0).toFixed(4)}
                    </strong>
                    {ws.profitMargin !== undefined && (
                      <div>
                        <small className="text-muted">({ws.profitMargin}%)</small>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {workspaceCallCosts.length === 0 && !loading && (
            <div className="text-center text-muted py-4">
              No workspace data available for the selected period.
            </div>
          )}

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, pagination.total)} of {pagination.total}
              </div>
              <Pagination>
                <PaginationItem disabled={currentPage === 1}>
                  <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                </PaginationItem>
                {[...Array(pagination.pages || 0)].map((_, i) => (
                  <PaginationItem active={i + 1 === currentPage} key={i}>
                    <PaginationLink onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage === pagination.pages}>
                  <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </div>
      </div>


      {/* Time Series Chart */}
      {callsAnalytics && callsAnalytics.timeSeriesData && callsAnalytics.timeSeriesData.length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <h5 className="mb-0">Calls & Cost Trends Over Time</h5>
          </CardHeader>
          <CardBody>
            <div style={{ height: '350px' }}>
              <Line
                data={timeSeriesData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { mode: 'index', intersect: false },
                  scales: {
                    y: {
                      type: 'linear',
                      position: 'left',
                      title: { display: true, text: 'Cost ($)' },
                      beginAtZero: true
                    },
                    y1: {
                      type: 'linear',
                      position: 'right',
                      title: { display: true, text: 'Calls Count' },
                      beginAtZero: true,
                      grid: { drawOnChartArea: false }
                    }
                  }
                }}
              />
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default CallsAnalytics;
